import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

export default function QRScanner({ onScan, onError }) {
  const scannerRef = useRef(null);
  const [active, setActive] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCam, setSelectedCam] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Html5Qrcode.getCameras().then(devices => {
      setCameras(devices);
      // Prefer back camera
      const back = devices.find(d => d.label.toLowerCase().includes("back") || d.label.toLowerCase().includes("rear") || d.label.toLowerCase().includes("environment"));
      setSelectedCam(back?.id || devices[0]?.id);
    }).catch(e => setError("Camera access denied. Please allow camera permission."));

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanner = async () => {
    if (!selectedCam) return;
    setError("");
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;

    try {
      await scanner.start(
        selectedCam,
        { fps: 20, qrbox: (w, h) => ({ width: Math.min(w, h) * 0.85, height: Math.min(w, h) * 0.85 }), aspectRatio: 1.0 },
        (decodedText) => {
          // Extract UID from URL if full URL was scanned
          const uid = decodedText.includes("/verify/")
            ? decodedText.split("/verify/")[1]
            : decodedText;
          onScan(uid.trim());
        },
        () => {} // ignore scan failures
      );
      setActive(true);
    } catch(e) {
      setError("Could not start camera: " + e.message);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      await scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setActive(false);
  };

  return (
    <div className="flex flex-col items-center">
      {/* Camera selector */}
      {cameras.length > 1 && (
        <select value={selectedCam || ""} onChange={e => setSelectedCam(e.target.value)}
          className="w-full text-xs text-gray-300 rounded-lg px-3 py-2 mb-3 focus:outline-none"
          style={{background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)"}}>
          {cameras.map(c => <option key={c.id} value={c.id}>{c.label || c.id}</option>)}
        </select>
      )}

      {/* Scanner viewport */}
      <div className="relative w-full rounded-2xl overflow-hidden" style={{background:"#000", minHeight:"360px", aspectRatio:"3/4"}}>
        <div id="qr-reader" className="w-full h-full"/>

        {/* Corner decorations */}
        {[["top-3 left-3","border-t-2 border-l-2"],["top-3 right-3","border-t-2 border-r-2"],
          ["bottom-3 left-3","border-b-2 border-l-2"],["bottom-3 right-3","border-b-2 border-r-2"]].map(([pos,brd])=>(
          <div key={pos} className={`absolute ${pos} w-8 h-8 ${brd} border-brand-400 rounded-sm z-10`}/>
        ))}

        {/* Scanning line animation */}
        {active && (
          <div className="absolute left-4 right-4 h-0.5 z-10"
            style={{background:"linear-gradient(90deg,transparent,#9b3fe8,transparent)", animation:"scan 2s linear infinite", top:"50%"}}/>
        )}

        {/* Overlay when not active */}
        {!active && (
          <div className="absolute inset-0 flex items-center justify-center" style={{background:"rgba(0,0,0,0.8)"}}>
            <div className="text-center">
              <p className="text-4xl mb-3">📷</p>
              <p className="text-gray-300 text-sm">Camera not active</p>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && <p className="text-red-400 text-xs mt-2 text-center">{error}</p>}

      {/* Controls */}
      <div className="flex gap-3 mt-4 w-full">
        {!active ? (
          <button onClick={startScanner} disabled={!selectedCam}
            className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition hover:scale-[1.02] disabled:opacity-50"
            style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
            📷 Start Camera
          </button>
        ) : (
          <button onClick={stopScanner}
            className="flex-1 py-3 rounded-xl text-white font-semibold text-sm transition"
            style={{background:"rgba(239,68,68,0.2)", border:"1px solid rgba(239,68,68,0.4)"}}>
            ⏹ Stop Camera
          </button>
        )}
      </div>

      <style>{`
        @keyframes scan {
          0% { top: 20%; }
          50% { top: 80%; }
          100% { top: 20%; }
        }
        #qr-reader { width: 100% !important; height: 100% !important; }
        #qr-reader video { width: 100% !important; height: 100% !important; object-fit: cover; position: absolute; top: 0; left: 0; }
        #qr-reader img { display: none !important; }
        #qr-reader__scan_region { width: 100% !important; height: 100% !important; position: absolute !important; top: 0 !important; left: 0 !important; }
        #qr-reader__scan_region > img { display: none !important; }
        #qr-reader__dashboard { display: none !important; }
        #qr-reader canvas { position: absolute; top: 0; left: 0; }
      `}</style>
    </div>
  );
}